import {
  formulasCheck,
  getFormulasFormulasQueryKey,
  useFormulasChange,
  useFormulasCreate,
  useFormulasFields,
  useFormulasFormulas,
  useFormulasPreview,
  useFormulasRemove,
  useFormulasRun,
} from "@api/generated/formulas/formulas";
import { useMeasurementsTables } from "@api/generated/measurements/measurements";
import type { FormulaIO, FormulaPreview, FormulaProblem, FormulaRun, SavedFormulaOut } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { hasRole, useSeasonFromLink } from "@app/workspace";
import { FormulaEditor } from "@features/trials/FormulaEditor";
import { FormulaPreviewCard } from "@features/trials/FormulaPreviewCard";
import { readableFields, writableFields } from "@features/trials/fields";
import { num } from "@ui/format";
import "@features/trials/formulas.css";
import { useTrial } from "@app/trials";
import { ResultBanner } from "@features/system/undo";
import { PlotRange } from "@features/trials/PlotRange";
import { TrialPicker } from "@features/trials/TrialPicker";
import { ActionIcon, Button, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChoiceList } from "@ui/ChoiceList";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { MissingList } from "@ui/RunBar";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/trials/formulas")({
  validateSearch: z.object({ ev: z.number().int().optional(), kis: z.string().optional(), th: z.string().optional() }),
  component: FormulasPage,
});

/** A formula as edited: its steps in order (the first is the API's ``formula``, the rest its ``steps``). */
type Draft = { id: number | null; name: string; description: string | null; steps: FormulaIO[] };
const EMPTY: FormulaIO = { mode: "compute", target: "", expression: "", rules: [], otherwise: null };
const MAX_STEPS = 20;
const draftOf = (s: SavedFormulaOut): Draft => ({
  id: s.id,
  name: s.name,
  description: s.description ?? null,
  steps: [s.formula, ...(s.steps ?? [])],
});
const body = (steps: FormulaIO[]) => ({ formula: steps[0] ?? EMPTY, steps: steps.slice(1) });

/** Kísérletek → Képletek: formulas over the measurement tables (the legacy Formula menu). A formula computes a column
 *  or classifies rows, in one or more steps run in order (a later step reads the earlier ones' results); it is checked
 *  as it is typed, previewed over a trial (every change listed), and run as one undoable operation. */
function FormulasPage() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = Route.useNavigate();
  const client = useQueryClient();
  const me = useUserMe().data;
  const canEdit = hasRole(me?.roles, "nemesito");
  const admin = hasRole(me?.roles, "admin");
  const ev = useSeasonFromLink(params.ev);
  const { trials, trial, th } = useTrial(ev, params.kis, params.th);
  const saved = useFormulasFormulas({ query: { enabled: canEdit } }).data ?? [];
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  // what a formula reads: the plot's own key (plot.alp …) and every table's fields
  const fields = useFormulasFields({ query: { staleTime: Number.POSITIVE_INFINITY, enabled: canEdit } }).data;
  const targets = useMemo(() => writableFields(tables ?? []), [tables]);
  const readable = useMemo(() => readableFields(fields ?? []), [fields]);
  const fresh = (): Draft => ({ id: null, name: t("formulas.newName"), description: null, steps: [EMPTY] });
  const [draft, setDraft] = useState<Draft>(fresh);
  const [range, setRange] = useState<[number | string, number | string]>(["", ""]);
  const [preview, setPreview] = useState<FormulaPreview | null>(null);
  const [deleting, setDeleting] = useState(false);

  // the last run, shown while the formula and the scope are the ones it ran (any change hides it)
  const [done, setDone] = useState<{ run: FormulaRun; key: string } | null>(null);
  const edit = (next: Draft) => {
    setDraft(next);
    setPreview(null);
  };
  const [checked] = useDebouncedValue(draft.steps, 300);
  // each step is checked on its own (a step's fields and target do not depend on the others)
  const problems: FormulaProblem[][] = useQueries({
    queries: checked.map((step) => ({
      queryKey: ["formula-check", step],
      enabled: canEdit && !!step.target,
      queryFn: ({ signal }: { signal: AbortSignal }) => formulasCheck(step, { signal }),
      placeholderData: (prev: FormulaProblem[] | undefined) => prev,
    })),
  }).map((q, i) => (checked[i]?.target ? (q.data ?? []) : []));
  const scope = { ev: ev ?? 0, kis: trial?.kis ?? null, th: th ?? null, parc_from: num(range[0]), parc_to: num(range[1]) };
  const allowed = !!trial || admin; // a whole season is for administrators
  const runKey = JSON.stringify([draft.steps, scope]);
  const result = done?.key === runKey ? done.run : null;
  const targeted = draft.steps.every((s) => !!s.target);
  const faultless = problems.every((p) => p.length === 0) && checked === draft.steps;
  const ready = targeted && faultless;
  const setStep = (i: number, step: FormulaIO) => edit({ ...draft, steps: draft.steps.map((s, j) => (j === i ? step : s)) });
  const moveStep = (i: number, by: number) => {
    const steps = [...draft.steps];
    const [moved] = steps.splice(i, 1);
    if (moved) steps.splice(i + by, 0, moved);
    edit({ ...draft, steps });
  };

  const previewing = useFormulasPreview({ mutation: { onSuccess: setPreview } });
  const running = useFormulasRun({
    mutation: {
      onSuccess: (out) => {
        setDone({ run: out, key: runKey });
        setPreview(null);
        void client.invalidateQueries({ queryKey: ["measurements"] });
      },
    },
  });
  const onSaved = (out: SavedFormulaOut) => {
    notifications.show({ color: "green", message: t("formulas.saved") });
    setDraft(draftOf(out));
    void client.invalidateQueries({ queryKey: getFormulasFormulasQueryKey() });
  };
  const create = useFormulasCreate({ mutation: { onSuccess: onSaved } });
  const change = useFormulasChange({ mutation: { onSuccess: onSaved } });
  const remove = useFormulasRemove({
    mutation: {
      onSuccess: () => {
        setDeleting(false);
        edit(fresh());
        void client.invalidateQueries({ queryKey: getFormulasFormulasQueryKey() });
      },
    },
  });
  const save = () => {
    const data = { name: draft.name, description: draft.description, ...body(draft.steps) };
    if (draft.id === null) create.mutate({ data });
    else change.mutate({ formulaId: draft.id, data });
  };

  if (!canEdit) {
    return (
      <div className="page">
        <PageHeader title={t("formulas.title")} />
        <p className="mu">{t("shell.breederOnly")}</p>
      </div>
    );
  }

  const current = saved.find((s) => s.id === draft.id);
  return (
    <div className="page fill">
      <PageHeader title={t("formulas.title")} season subtitle={t("formulas.subtitle")} />
      <div className="hstack grow" style={{ gap: 16, alignItems: "stretch", minHeight: 0 }}>
        <ChoiceList
          title={t("formulas.formulaSets")}
          action={
            <Button size="compact-xs" variant="default" onClick={() => edit(fresh())}>
              {t("formulas.addNew")}
            </Button>
          }
          empty={t("formulas.emptyList")}
          items={saved.map((s) => ({
            id: s.id,
            title: s.name,
            sub: s.steps?.length
              ? `${t("formulas.stepCount", { count: s.steps.length + 1 })} · ${s.formula.target}`
              : `${t(`formulas.modes.${s.formula.mode}`)} · ${s.formula.target}`,
          }))}
          value={draft.id}
          onChange={(id) => {
            const found = saved.find((s) => s.id === id);
            if (found) edit(draftOf(found));
          }}
        />

        <div className="stack grow" style={{ gap: 14, minWidth: 0, overflowY: "auto" }}>
          <section className="card pad stack" style={{ gap: 12 }}>
            <span className="sec">{t("formulas.stepFormula")}</span>
            <div className="hstack" style={{ gap: 12, alignItems: "flex-end" }}>
              <TextInput
                size="xs"
                w={260}
                label={t("formulas.name")}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
              />
              <span className="grow sm mu">
                {current?.owner && t("formulas.owner", { user: current.owner })}
                {current?.legacy_source && ` · ${t("formulas.legacySource", { source: current.legacy_source })}`}
              </span>
              {draft.id !== null && (
                <Button size="xs" variant="default" color="red" onClick={() => setDeleting(true)}>
                  {t("formulas.delete")}
                </Button>
              )}
              <Button size="xs" loading={create.isPending || change.isPending} disabled={!draft.name.trim() || !ready} onClick={save}>
                {t("formulas.save")}
              </Button>
            </div>
            {draft.steps.map((step, i) => {
              const several = draft.steps.length > 1;
              const editor = (
                <FormulaEditor
                  value={step}
                  onChange={(next) => setStep(i, next)}
                  targets={targets}
                  fields={readable}
                  problems={problems[i] ?? []}
                  hits={preview?.steps[i]?.hits ?? null}
                  targetId={i ? `formula-target-${i}` : "formula-target"}
                />
              );
              if (!several) return <div key="only">{editor}</div>;
              const n = i + 1;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: steps are identified by their place (their order is their meaning)
                <div key={i} className="formula-step stack" style={{ gap: 8 }}>
                  <div className="hstack" style={{ gap: 6 }}>
                    <span className="sec grow">{t("formulas.step", { n })}</span>
                    <ActionIcon
                      variant="subtle"
                      disabled={i === 0}
                      aria-label={t("formulas.moveStepUp", { n })}
                      onClick={() => moveStep(i, -1)}
                    >
                      <Icon name="arrowUp" size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      disabled={i === draft.steps.length - 1}
                      aria-label={t("formulas.moveStepDown", { n })}
                      onClick={() => moveStep(i, 1)}
                    >
                      <Icon name="arrowDown" size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      aria-label={t("formulas.deleteStep", { n })}
                      onClick={() => edit({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })}
                    >
                      <Icon name="close" size={14} />
                    </ActionIcon>
                  </div>
                  {editor}
                </div>
              );
            })}
            <div className="hstack" style={{ gap: 10 }}>
              <Button
                size="compact-xs"
                variant="subtle"
                disabled={draft.steps.length >= MAX_STEPS}
                onClick={() => edit({ ...draft, steps: [...draft.steps, EMPTY] })}
              >
                {t("formulas.addStep")}
              </Button>
              {draft.steps.length > 1 && <span className="sm mu">{t("formulas.stepsHelp")}</span>}
            </div>
          </section>

          <section className="card pad" style={{ flexDirection: "row", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
            <div>
              <span className="sec">{t("formulas.stepApply")}</span>
              <div className="hstack" style={{ gap: 6, marginTop: 6 }}>
                <span className="chip g">{ev}</span>
              </div>
            </div>
            <TrialPicker
              trials={trials}
              trial={trial}
              th={th}
              onChange={(kis, site) => navigate({ search: (s) => ({ ...s, kis, th: site }) })}
            />
            <PlotRange
              size="xs"
              from={range[0]}
              to={range[1]}
              onFrom={(v) => setRange([v, range[1]])}
              onTo={(v) => setRange([range[0], v])}
              fromLabel={t("formulas.plotsFrom")}
              toLabel={t("formulas.plotsTo")}
            />
            <div className="grow" />
            <MissingList
              requirements={[
                { label: t("formulas.needTarget"), done: targeted, field: "formula-target" },
                { label: t("formulas.needValidFormula"), done: faultless },
                { label: t("formulas.needTrial"), done: allowed },
              ]}
            />
            <Button
              variant="default"
              disabled={!ready || !allowed}
              loading={previewing.isPending}
              onClick={() => previewing.mutate({ data: { ...body(draft.steps), scope } })}
            >
              {t("formulas.refreshPreview")}
            </Button>
          </section>
          {result && (
            <ResultBanner operation={result.operation}>
              <b>{result.written ? t("formulas.written", { count: formatNumber(result.written) }) : t("formulas.nothingToWrite")}</b>
            </ResultBanner>
          )}

          {preview && (
            <FormulaPreviewCard
              preview={preview}
              fields={fields ?? []}
              running={running.isPending}
              onRun={() => running.mutate({ data: { ...body(draft.steps), scope, token: preview.token, name: draft.name } })}
            />
          )}
        </div>
      </div>
      {deleting && draft.id !== null && (
        <ConfirmModal
          title={t("formulas.delete")}
          text={t("formulas.deleteConfirm", { name: draft.name })}
          confirm={t("formulas.delete")}
          danger
          loading={remove.isPending}
          onConfirm={() => draft.id !== null && remove.mutate({ formulaId: draft.id })}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
}
